import React, { useState, useEffect } from 'react';
import { Navbar as BSNavbar, Nav, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';

// Throttle function to limit scroll event frequency
function throttle(func: Function, limit: number) {
  let inThrottle: boolean;
  return function(this: any, ...args: any[]) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
}

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const isScrolled = scrollPosition > 20;
      setScrolled(isScrolled);

      // Update active section based on scroll position
      const sections = ['home', 'propuesta', 'beneficios', 'faq', 'contacto'];
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    const throttledScrollHandler = throttle(handleScroll, 16); // ~60fps
    window.addEventListener('scroll', throttledScrollHandler);
    
    return () => window.removeEventListener('scroll', throttledScrollHandler);
  }, []);

  // Handle window resize to reset menu state
  useEffect(() => {
    const handleResize = () => {
      // Reset menu state when window is resized to desktop size
      if (window.innerWidth >= 992) { // Bootstrap lg breakpoint
        setExpanded(false);
      }
    };

    // Check initial size
    handleResize();
    
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavClick = (sectionId: string) => {
    setActiveSection(sectionId);
    setExpanded(false); // Close menu when clicking nav item
    
    // Smooth scroll to section
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 80; // Account for navbar height
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  const navItems = [
    { id: 'home', label: 'Inicio' },
    { id: 'propuesta', label: 'Qué hacemos' },
    { id: 'beneficios', label: 'Beneficios' },
    { id: 'faq', label: 'Preguntas frecuentes' },
    { id: 'contacto', label: 'Contacto' }
  ];

  return (
    <BSNavbar
      expand="lg"
      fixed="top"
      className={`navbar-hyper ${scrolled ? 'scrolled' : ''}`}
      expanded={expanded}
    >
      <Container>
        <BSNavbar.Brand as={Link} to="/" className="brand-hyper" style={{ display: 'flex', alignItems: 'center' }}>
          <img 
            src="/logo.png" 
            alt="Arrank Logo" 
            style={{ 
              height: scrolled ? '40px' : '48px',
              marginRight: scrolled ? '16px' : '20px',
              transition: 'all 0.3s ease'
            }} 
          />
          Arrank!
        </BSNavbar.Brand>
        <BSNavbar.Toggle 
          aria-controls="basic-navbar-nav" 
          className={`toggle-hyper ${expanded ? 'active' : ''}`} 
          onClick={() => setExpanded(!expanded)}
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </BSNavbar.Toggle>
        <BSNavbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto align-items-center">
            {navItems.map((item, index) => (
              <Nav.Link
                key={item.id}
                href={`#${item.id}`}
                className={`link-hyper ${activeSection === item.id ? 'active' : ''}`}
                onClick={e => {
                  e.preventDefault();
                  handleNavClick(item.id);
                }}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className="link-text">{item.label}</span>
                <span className="link-underline"></span>
              </Nav.Link>
            ))}
          </Nav>
        </BSNavbar.Collapse>
      </Container>
    </BSNavbar>
  );
}

export default Navbar;
